/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * AgentHeader
 * Modernised header for the Agent Detail screen. Combines identity, status,
 * primary actions, and a secondary action menu into a single responsive bar.
 */

import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Divider,
  IconButton,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Chat as ChatIcon,
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as ExportIcon,
  Info as InfoIcon,
  MoreVert as MoreIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

import type { AgentDetail as AgentDetailType } from '../../../types';
import { AgentSyncButton } from '../AgentSyncButton';
import { getAgentTypeMeta, getInitials, hashHue } from './agentTypeMeta';

interface AgentHeaderProps {
  agent: AgentDetailType;
  isStaff: boolean;
  onChat: () => void;
  onEdit: () => void;
  onClone: () => void;
  onExport: () => void;
  onDelete: () => void;
  onToggleActive: (next: boolean) => void;
  cloning: boolean;
  exporting: boolean;
  toggling: boolean;
}

export const AgentHeader: React.FC<AgentHeaderProps> = ({
  agent,
  isStaff,
  onChat,
  onEdit,
  onClone,
  onExport,
  onDelete,
  onToggleActive,
  cloning,
  exporting,
  toggling,
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const typeMeta = getAgentTypeMeta(agent.agent_type);
  const hue = hashHue(agent.name || `${agent.id}`);
  const accent = `hsl(${hue}, 65%, 55%)`;
  const initials = getInitials(agent.name);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 1.5 }}
        aria-label="breadcrumb"
      >
        <MuiLink component={RouterLink} to="/ai/agents" underline="hover" color="inherit">
          Agents
        </MuiLink>
        <Typography color="text.primary" noWrap sx={{ maxWidth: 320 }}>
          {agent.name}
        </Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 2, md: 3 },
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          backgroundImage: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, transparent 55%)`,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Avatar
            sx={{
              bgcolor: accent,
              color: theme.palette.getContrastText(accent),
              width: { xs: 52, sm: 64 },
              height: { xs: 52, sm: 64 },
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 600,
              border: `2px solid ${alpha(accent, 0.4)}`,
            }}
          >
            {initials}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              noWrap
              sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
            >
              {agent.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mt: 0.25,
              }}
            >
              {agent.description || 'No description provided.'}
            </Typography>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1, rowGap: 0.5 }}>
              <Chip size="small" color={typeMeta.color} label={typeMeta.label} />
              {agent.is_template ? (
                <Chip size="small" color="warning" icon={<InfoIcon />} label="Template" />
              ) : agent.template_info?.is_template_based ? (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  icon={<InfoIcon />}
                  label={
                    agent.template_info.needs_sync
                      ? 'Template — needs sync'
                      : 'From Template'
                  }
                />
              ) : null}
              {agent.model?.name ? (
                <Tooltip title={`LLM: ${agent.model.name} (${agent.model.provider_name || '—'})`}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`LLM: ${agent.model.name}`}
                    sx={{ maxWidth: 260 }}
                  />
                </Tooltip>
              ) : null}
              {agent.embedding_model?.name ? (
                <Tooltip
                  title={`Embeddings: ${agent.embedding_model.name} (${agent.embedding_model.provider_name || '—'}${
                    agent.embedding_model.dimensions ? `, ${agent.embedding_model.dimensions}d` : ''
                  })`}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    color="secondary"
                    label={`Embed: ${agent.embedding_model.name}`}
                    sx={{ maxWidth: 260 }}
                  />
                </Tooltip>
              ) : null}
            </Stack>
          </Box>
        </Stack>

        <Divider
          flexItem
          orientation={isSmDown ? 'horizontal' : 'vertical'}
          sx={{ display: { xs: 'block', md: 'block' } }}
        />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ rowGap: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title={agent.is_active ? 'Agent is active — click to deactivate' : 'Agent is inactive — click to activate'}>
              <Switch
                size="small"
                checked={agent.is_active}
                onChange={(e) => onToggleActive(e.target.checked)}
                disabled={toggling}
                color="success"
              />
            </Tooltip>
            <Typography
              variant="caption"
              color={agent.is_active ? 'success.main' : 'text.secondary'}
              fontWeight={600}
            >
              {agent.is_active ? 'Active' : 'Inactive'}
            </Typography>
          </Stack>

          <AgentSyncButton agent={agent} variant="button" size="small" />

          <Button
            variant="contained"
            startIcon={<ChatIcon />}
            onClick={onChat}
            size="small"
          >
            Chat
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
            size="small"
          >
            Edit
          </Button>

          <Tooltip title="More actions">
            <IconButton size="small" onClick={openMenu} aria-label="More actions">
              <MoreIcon />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            slotProps={{ paper: { sx: { minWidth: 200 } } }}
          >
            <MenuItem
              onClick={() => {
                closeMenu();
                onClone();
              }}
              disabled={cloning}
            >
              <ListItemIcon>
                <CloneIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Clone agent</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                onExport();
              }}
              disabled={exporting}
            >
              <ListItemIcon>
                <ExportIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export JSON</ListItemText>
            </MenuItem>
            {isStaff ? <Divider /> : null}
            <MenuItem
              onClick={() => {
                closeMenu();
                onDelete();
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete agent</ListItemText>
            </MenuItem>
          </Menu>
        </Stack>
      </Box>
    </Box>
  );
};

export default AgentHeader;
